import React, { useState, useRef, FC, Ref, LegacyRef } from 'react';
import classNames from 'classnames';
import { JourneyMenu } from 'src/common/JourneyMenu';
import { Nullable } from 'src/types/nullable.type';

import { Draggable } from 'react-beautiful-dnd';
import { ClickAwayListener } from '@material-ui/core';

import { TooltipComponent } from 'src/common/tooltip/component';

import { usePopper } from 'react-popper';

import {
  TextCapitaliseStrongIcon,
  FrameImageStrongIcon,
  BoldStrongIcon,
  ChecklistStrongIcon,
  LinkStrongIcon,
  TableStrongIcon,
  FrameImageRegularIcon,
  AddStrongIcon,
} from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';

import { useEditorOptions } from './use-editor-options.hook';

import { TextEditorImageOptionsMenu } from './image-options-menu';

import { apiGetEmbedDetailsFromUrl } from 'src/utils/journeyApi';

import { DocumentBlockType } from 'src/common/interfaces/document_block.interface';

interface TextEditorNewBlockMenuProps {
  editor: any;
  onAddBlock: (blockType: DocumentBlockType, attrs: any) => void;
  onClickAddEmptyBlock?: () => void;
  isDraggingOverToEditor: boolean;
}

export const TextEditorNewBlockMenu: FC<TextEditorNewBlockMenuProps> = ({
  editor,
  onAddBlock,
  onClickAddEmptyBlock,
  isDraggingOverToEditor = false,
}) => {
  const popperElementRef = useRef<Nullable<HTMLDivElement>>(null);
  const [targetElement, setTargetElement] = useState<HTMLDivElement>();
  const [popperElement, setPopperElement] = useState<Nullable<HTMLDivElement>>();

  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: 'left',
    modifiers: [{ name: 'offset', options: { offset: [0, 24] } }],
  });

  const getButtonClasses = (isDragging = false, isDraggingOverToEditor = false) => {
    return classNames('p-2 rounded transition-all duration-200 text-white', {
      'bg-bedrock-black text-white': !(isDragging && isDraggingOverToEditor),
      'bg-bedrock-white !text-bedrock-black !w-[240px] flex justify-center': isDragging && isDraggingOverToEditor,
    });
  };

  const getDraggableButtonClasses = (isDragging = false, isDraggingOverToEditor = false) => {
    return classNames('w-full p-4 rounded bg-white text-bedrock-black hover:opacity-80');
  };

  const blockOptions = [
    {
      hoverable: false,
      icon: <TextCapitaliseStrongIcon />,
      text: 'Paragraph',
      draggableId: 'paragraph',
      attrs: {},
      index: 0,
      items: [],
      tooltipContent: 'Add text',
    },
    {
      draggableId: 'image',
      attrs: {},
      type: 'image',
      icon: <FrameImageStrongIcon />,
      text: 'Image',
      tooltipContent: 'Add an image',
      index: 4,
    },
    {
      draggableId: 'iframe',
      attrs: {},
      type: 'iframe',
      icon: <LinkStrongIcon />,
      text: 'Embed',
      tooltipContent: 'Add an embed from any website',
      index: 5,
    },
  ];

  return (
    <ClickAwayListener onClickAway={() => setPopperElement(null)}>
      <div className='flex flex-col bg-bedrock-black p-2 rounded-lg'>
        {blockOptions.map(({ draggableId, type, attrs, icon, text, hoverable, items, index, tooltipContent }) => {
          if (hoverable && items && items.length > 0) {
            return (
              <div
                ref={setTargetElement as unknown as Ref<HTMLDivElement>}
                onMouseEnter={(e) => {
                  setPopperElement(e.currentTarget);
                }}
              >
                <button
                  type='button'
                  className={getButtonClasses()}
                  onClick={() => onAddBlock(type as DocumentBlockType, attrs || {})}
                >
                  {icon}
                </button>
                <div
                  ref={popperElementRef as unknown as LegacyRef<HTMLDivElement>}
                  className={classNames('w-[240px]', {
                    hidden: !popperElement,
                  })}
                  style={styles.popper}
                  {...attributes.popper}
                >
                  <div className='flex flex-col space-y-2'>
                    {(items || []).map(({ draggableId, type, attrs, icon, text }) => (
                      <Draggable key={`block-menu-${draggableId}-${type}`} draggableId={draggableId} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            data-is-dragging={snapshot.isDragging}
                            style={provided.draggableProps.style}
                          >
                            <button
                              type='button'
                              className={getDraggableButtonClasses(snapshot.isDragging, isDraggingOverToEditor)}
                              onClick={() => {
                                onAddBlock(type as DocumentBlockType, attrs || {});
                                setPopperElement(null);
                              }}
                            >
                              <div className='flex items-center space-x-2'>
                                {icon}
                                <div className='text-bedrock-p text-bedrock-black'>{text}</div>
                              </div>
                            </button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <TooltipComponent placement='left' tooltipContent={tooltipContent} theme='journey'>
              <Draggable key={`block-menu-${type}`} draggableId={draggableId} index={index}>
                {(provided, snapshot) => {
                  return (
                    <>
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        data-is-dragging={snapshot.isDragging}
                        style={provided.draggableProps.style}
                      >
                        <button
                          type='button'
                          className={getButtonClasses(snapshot.isDragging, isDraggingOverToEditor)}
                          onClick={() => onAddBlock(type as DocumentBlockType, attrs || {})}
                          onMouseEnter={() => setPopperElement(null)}
                        >
                          {icon}
                        </button>
                      </div>
                      {snapshot.isDragging && (
                        <button type='button' className={getButtonClasses()}>
                          {icon}
                        </button>
                      )}
                    </>
                  );
                }}
              </Draggable>
            </TooltipComponent>
          );
        })}
      </div>
    </ClickAwayListener>
  );
};
